import React, { useState } from "react";
import { toast } from "sonner";
import { uploadFileChunked, fileUrl } from "@/lib/api";
import { Youtube, Video, Image as ImageIcon, FileText, Type, Upload, GripVertical, X, Link as LinkIcon } from "lucide-react";

const area = { width: "100%", background: "var(--ink)", border: "1px solid var(--line)", color: "var(--cream)", padding: "9px 12px", fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 13, borderRadius: 8 };

function MiniUploader({ accept, label, testid, onDone }) {
  const [pct, setPct] = useState(null);
  const onChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPct(0);
    let dur = 0;
    if (file.type.startsWith("video/")) {
      dur = await new Promise((resolve) => {
        const v = document.createElement("video");
        v.preload = "metadata";
        v.onloadedmetadata = () => { URL.revokeObjectURL(v.src); resolve(Math.round(v.duration) || 0); };
        v.onerror = () => resolve(0);
        v.src = URL.createObjectURL(file);
      });
    }
    try {
      const res = await uploadFileChunked(file, setPct);
      onDone(res, dur);
      toast.success("Archivo subido ✓");
    } catch { toast.error("Error al subir el archivo"); }
    finally { setPct(null); e.target.value = ""; }
  };
  return (
    <label className="abtn" data-testid={testid} style={{ cursor: pct != null ? "wait" : "pointer", display: "inline-flex", gap: 6, alignItems: "center" }}>
      <Upload size={13} /> {pct == null ? label : `Subiendo… ${pct}%`}
      <input type="file" accept={accept} style={{ display: "none" }} onChange={onChange} disabled={pct != null} />
    </label>
  );
}

const TIPO_LABEL = { youtube: "YOUTUBE", vimeo: "VIMEO", video: "VÍDEO", imagen: "IMAGEN", pdf: "PDF", texto: "TEXTO", enlace: "ENLACE" };

export default function Bloques({ value = [], onChange, tid = "b" }) {
  const [drag, setDrag] = useState(null);
  const list = value || [];
  const add = (b) => onChange([...list, b]);
  const upd = (i, patch) => onChange(list.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const del = (i) => onChange(list.filter((_, j) => j !== i));
  const drop = (i) => {
    if (drag === null || drag === i) { setDrag(null); return; }
    const a = [...list]; const [m] = a.splice(drag, 1); a.splice(i, 0, m); onChange(a); setDrag(null);
  };

  return (
    <div data-testid={`bloques-${tid}`}>
      {list.map((b, i) => (
        <div key={i} draggable data-testid={`bloque-${tid}-${i}`}
          onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => drop(i)}
          style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8, padding: "8px 10px", border: "1px solid var(--line)", borderRadius: 8, background: drag === i ? "rgba(47,107,255,.10)" : "rgba(255,255,255,.02)", cursor: "grab" }}>
          <GripVertical size={15} style={{ color: "var(--muted)", marginTop: 6, flexShrink: 0 }} />
          <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: ".08em", color: "var(--gold)", minWidth: 52, marginTop: 8 }}>{TIPO_LABEL[b.tipo] || b.tipo}</span>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            {(b.tipo === "youtube" || b.tipo === "vimeo") && (
              <input value={b.url || ""} onChange={(e) => upd(i, { url: e.target.value })} data-testid={`bloque-url-${tid}-${i}`}
                placeholder={b.tipo === "youtube" ? "https://youtube.com/watch?v=..." : "https://vimeo.com/..."} style={area} />
            )}
            {b.tipo === "video" && (
              b.file_url
                ? <div style={{ display: "flex", alignItems: "center", gap: 10 }}><span style={{ fontSize: 12, color: "var(--muted)" }}>Vídeo subido ✓{b.dur ? ` · ${Math.floor(b.dur / 60)}m ${b.dur % 60}s` : ""}</span>
                    <MiniUploader accept="video/*" label="Reemplazar" testid={`bloque-upload-${tid}-${i}`} onDone={(res, dur) => upd(i, { file_url: res.url, dur })} /></div>
                : <MiniUploader accept="video/*" label="Subir vídeo" testid={`bloque-upload-${tid}-${i}`} onDone={(res, dur) => upd(i, { file_url: res.url, dur })} />
            )}
            {b.tipo === "imagen" && (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {b.file_url && <img src={fileUrl(b.file_url)} alt="" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 8, border: "1px solid var(--line)" }} />}
                <MiniUploader accept="image/*" label={b.file_url ? "Reemplazar" : "Subir imagen"} testid={`bloque-upload-${tid}-${i}`} onDone={(res) => upd(i, { file_url: res.url })} />
              </div>
            )}
            {b.tipo === "pdf" && (
              <>
                <input value={b.titulo || ""} onChange={(e) => upd(i, { titulo: e.target.value })} placeholder="Nombre del documento" style={area} />
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {b.file_url && <span style={{ fontSize: 12, color: "var(--muted)" }}>PDF cargado ✓</span>}
                  <MiniUploader accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.zip,.rar,.7z,.html,.htm,.txt,.csv,.json,.js,.css" label={b.file_url ? "Reemplazar" : "Subir archivo (PDF, HTML, ZIP…)"} testid={`bloque-upload-${tid}-${i}`}
                    onDone={(res) => upd(i, { file_url: res.url, titulo: b.titulo || res.filename })} />
                </div>
              </>
            )}
            {b.tipo === "texto" && (
              <textarea value={b.texto || ""} onChange={(e) => upd(i, { texto: e.target.value })} data-testid={`bloque-texto-${tid}-${i}`}
                placeholder="Escribe el texto que verá el alumno…" style={{ ...area, minHeight: 80 }} />
            )}
            {b.tipo === "enlace" && (
              <>
                <input value={b.titulo || ""} onChange={(e) => upd(i, { titulo: e.target.value })} data-testid={`bloque-enlace-titulo-${tid}-${i}`}
                  placeholder="Nombre del enlace (ej: Documentación, Herramienta…)" style={area} />
                <input value={b.url || ""} onChange={(e) => upd(i, { url: e.target.value })} data-testid={`bloque-enlace-url-${tid}-${i}`}
                  placeholder="https://…" style={area} />
              </>
            )}
          </div>
          <button type="button" className="abtn danger" onClick={() => del(i)} data-testid={`bloque-del-${tid}-${i}`} style={{ flexShrink: 0 }}><X size={13} /></button>
        </div>
      ))}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
        <button type="button" className="abtn" data-testid={`add-youtube-${tid}`} onClick={() => add({ tipo: "youtube", url: "" })}><Youtube size={13} /> YouTube</button>
        <button type="button" className="abtn" data-testid={`add-vimeo-${tid}`} onClick={() => add({ tipo: "vimeo", url: "" })}><Video size={13} /> Vimeo</button>
        <button type="button" className="abtn" data-testid={`add-video-${tid}`} onClick={() => add({ tipo: "video", file_url: "" })}><Upload size={13} /> Subir vídeo</button>
        <button type="button" className="abtn" data-testid={`add-imagen-${tid}`} onClick={() => add({ tipo: "imagen", file_url: "" })}><ImageIcon size={13} /> Imagen</button>
        <button type="button" className="abtn" data-testid={`add-pdf-${tid}`} onClick={() => add({ tipo: "pdf", file_url: "", titulo: "" })}><FileText size={13} /> Archivo / HTML</button>
        <button type="button" className="abtn" data-testid={`add-texto-${tid}`} onClick={() => add({ tipo: "texto", texto: "" })}><Type size={13} /> Texto</button>
        <button type="button" className="abtn" data-testid={`add-enlace-${tid}`} onClick={() => add({ tipo: "enlace", url: "", titulo: "" })}><LinkIcon size={13} /> Enlace web</button>
      </div>
    </div>
  );
}
